import { useMemo, type ReactNode } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { MetricBlock } from "../primitives/MetricBlock";
import {
  formatE1rm,
  formatVolume,
  formatWeight,
  liftScore,
  muscleKey,
  normalizeVolumeByMuscle,
  pickHeadlineDecision,
  formatDuration,
  formatDensity,
  type CatalogMuscleSlug,
  type SessionResult,
  type WorkoutNote,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { Icon } from "../Icon/Icon";
import { exerciseTrackLabel } from "../Logger/exerciseTrackLabel";
import { makeTimelineRowText } from "../Logger/timelineRowText";
import { makeStyles } from "./sessionSummaryStyles";
import { weightUnitLabels } from "../weightUnitLabels";

// THE session summary surface — read-only display of a computed SessionResult,
// one tree for the mobile post-save card and the web console detail pane. Four
// layers: headline (PR > big delta > solid > fallback), metric blocks (top
// lifts / volume / duration / density), volume-by-muscle bars, and the dual
// bodymap. The bodymap (body-highlighter, native-only) and the share
// affordance (view-shot capture, native-only) are injected as host slots;
// everything else is RN-primitive + tokens. Reuses the existing
// `mobile.postSession.*` i18n keys so no new strings are needed.

export type SessionSummaryViewProps = {
  theme: UiTheme;
  result: SessionResult;
  locale: string;
  unit: "kg" | "lb";
  /** Resolve a display name from an exercise slug (FK → live catalog); the
   *  snapshot exerciseName is the deleted-row fallback. */
  resolveName: (slug: string, fallback?: string) => string;
  /** Dual front+back heatmap (body-highlighter, works on RNW). Each mount
   *  injects its own theme-wired <BodyMap>. When unset the body block is not
   *  rendered. */
  bodyMapSlot?: ReactNode;
  /** Share affordance — both mounts inject a real <Button> wired to the
   *  shared capture pipeline (packages/ui/src/Logger/{ShareCardRenderer,
   *  useShareCardHandle}.ts, 2026-08-18); only the post-capture Share step
   *  differs per platform. */
  shareSlot?: ReactNode;
  /** Energy readout (calories estimate-vs-measured + effort/HR). Each mount
   *  builds its own <CaloriesEffortBlock> — it needs the full Session's measured
   *  calories/HR + the mount's hcWins preference, data this shared view doesn't
   *  hold — and injects it here, same slot pattern as bodyMapSlot/shareSlot. */
  energySlot?: ReactNode;
  /** Edit affordance. A past session's summary (mobile WorkoutSummaryScreen,
   *  web-rnw detail pane, #318) injects a <Button> here that opens the logger
   *  in edit. The post-save card leaves it unset. */
  actionSlot?: ReactNode;
  /** The session's timeline notes, oldest first (#24). The host reads them
   *  (useSessionNotes, or the draft after a save); this view only shows them. */
  notes?: readonly SummaryNote[];
};

/** What the notes block reads: a saved note or a draft one. */
export type SummaryNote = Pick<WorkoutNote, "id" | "text" | "performedAt">;

type HeadlineParts =
  | { kind: "pr"; label: string; name: string; value: string }
  | { kind: "delta"; label: string; name: string; value: string }
  | { kind: "solid"; label: string; value: string }
  | { kind: "fallback"; label: string };

export function SessionSummaryView({
  theme,
  result,
  locale,
  unit,
  resolveName,
  bodyMapSlot,
  shareSlot,
  energySlot,
  actionSlot,
  notes = [],
}: SessionSummaryViewProps) {
  const { t } = useTranslation();
  // The translated unit word for every weight below (CN-26 ondata 2).
  // ⛔ MEMOISED on `t`, not rebuilt per render: the object below feeds a
  // `useMemo` dependency array, and a fresh identity every render would turn
  // that memo into a no-op. `t` changes when the language does, which is
  // exactly when these labels must change.
  const unitLabels = useMemo(() => weightUnitLabels(t), [t]);
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const noteTime = useMemo(() => makeTimelineRowText(t, locale).timeFmt, [t, locale]);

  // Every exercise with a weighted top set, ranked by the shared liftScore
  // (e1RM when it qualified, otherwise absolute load). Capped at 6 to keep the
  // card compact; further rows still appear in the bars + bodymap.
  const topLifts = useMemo(
    () =>
      result.exerciseResults
        .filter((er) => er.topSet !== null && er.topSet.weightKg > 0)
        .sort((a, b) => liftScore(b) - liftScore(a))
        .slice(0, 6),
    [result.exerciseResults],
  );

  const catalogVolume = useMemo(
    () => normalizeVolumeByMuscle(result.volumeByMuscle),
    [result.volumeByMuscle],
  );

  const topMuscles = useMemo(() => {
    return (Object.entries(catalogVolume) as Array<[CatalogMuscleSlug, number]>)
      .filter(([, v]) => typeof v === "number" && v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [catalogVolume]);

  const maxMuscleVolume = topMuscles[0]?.[1] ?? 0;

  const headlineDecision = useMemo(() => pickHeadlineDecision(result), [result]);

  // Renders the shared decision (pickHeadlineDecision, @heraion/shared) —
  // this view owns i18n + unit formatting, the decision just says WHICH
  // headline. "solidDuration" is new (RW-2/RW-17): a session with no
  // weighted volume but a real duration (cardio/timed-only) used to fall
  // through to "fallback" and its "{{sets}} sets" wording, which read as a
  // lying "0 sets" — countsFromMetrics (setMetrics.ts) deliberately excludes
  // cardio sets from setCount, by design, for effort-coverage accounting,
  // but that exclusion should never surface as "this session had no sets."
  const headlineParts = useMemo<HeadlineParts>(() => {
    switch (headlineDecision.kind) {
      case "pr":
        return {
          kind: "pr",
          label: t("mobile.postSession.headlineLabelPR", {
            defaultValue: "NEW E1RM",
          }),
          name: resolveName(
            headlineDecision.exercise.exerciseSlug,
            headlineDecision.exercise.exerciseName,
          ),
          value: formatE1rm(headlineDecision.exercise.topE1rmKg as number, unit, locale, unitLabels),
        };
      case "delta":
        return {
          kind: "delta",
          label: t("mobile.postSession.headlineLabelDelta", {
            defaultValue: "BIG JUMP +{{delta}}",
            delta: formatE1rm(
              headlineDecision.exercise.deltaE1rmKg as number,
              unit,
              locale,
              unitLabels,
            ),
          }),
          name: resolveName(
            headlineDecision.exercise.exerciseSlug,
            headlineDecision.exercise.exerciseName,
          ),
          value: formatE1rm(headlineDecision.exercise.topE1rmKg as number, unit, locale, unitLabels),
        };
      case "solidVolume":
        return {
          kind: "solid",
          label: t("mobile.postSession.headlineLabelSolid", {
            defaultValue: "SOLID SESSION",
          }),
          value: t("mobile.postSession.headlineSolidValue", {
            defaultValue: "{{volume}} in {{duration}}",
            volume: formatVolume(headlineDecision.totalVolumeKg, unit, locale, unitLabels),
            duration: formatDuration(headlineDecision.durationMinutes),
          }),
        };
      case "solidDuration":
        return {
          kind: "solid",
          label: t("mobile.postSession.headlineLabelSolid", {
            defaultValue: "SOLID SESSION",
          }),
          value: formatDuration(headlineDecision.durationMinutes),
        };
      case "fallback":
        return {
          kind: "fallback",
          label: t("mobile.postSession.headlineFallback", {
            defaultValue: "{{label}} — {{sets}} sets",
            label: result.sessionLabel,
            sets: headlineDecision.totalSets,
          }),
        };
    }
  }, [
    headlineDecision,
    result.sessionLabel,
    t,
    unit,
    locale,
    resolveName,
    unitLabels,
  ]);

  return (
    <View style={styles.card}>
      {/* Layer 1 — Headline */}
      <View style={styles.headlineBlock}>
        <Text style={styles.sessionLabel}>{result.sessionLabel}</Text>
        <Text
          style={[
            styles.headlineLabel,
            (headlineParts.kind === "pr" || headlineParts.kind === "delta") &&
              styles.headlineLabelEarned,
          ]}
        >
          {headlineParts.label}
        </Text>
        {headlineParts.kind === "pr" || headlineParts.kind === "delta" ? (
          <View style={styles.headlineRow}>
            <Text style={styles.headlineName} numberOfLines={2}>
              {headlineParts.name}
            </Text>
            <Text style={styles.headlineValue} numberOfLines={1}>
              {headlineParts.value}
            </Text>
          </View>
        ) : headlineParts.kind === "solid" ? (
          <Text style={styles.headlineValue}>{headlineParts.value}</Text>
        ) : null}
      </View>

      {actionSlot}

      {/* Layer 2 — Metric blocks */}
      {topLifts.length > 0 ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockTopLifts", { defaultValue: "Top Lifts" })}
        >
          {topLifts.map((er) => (
            // EQ-1-HIST — one row per (exercise, machine), so the slug alone is
            // no longer unique within a session: logging one exercise on two
            // machines used to give two rows the SAME React key and the SAME
            // name, indistinguishable on screen.
            <View
              key={`${er.exerciseSlug}\u0000${er.equipmentInstance ?? ""}`}
              style={styles.champRow}
            >
              <View style={styles.champMain}>
                <Text style={styles.champName} numberOfLines={1}>
                  {exerciseTrackLabel(
                    resolveName(er.exerciseSlug, er.exerciseName),
                    er.equipmentInstance,
                    t,
                  )}
                </Text>
                {er.topSet ? (
                  <Text style={styles.champFrom} numberOfLines={1}>
                    {t("mobile.postSession.fromSet", {
                      defaultValue: "from {{weight}} × {{reps}}",
                      weight: formatWeight(er.topSet.weightKg, unit, locale, unitLabels),
                      reps: er.topSet.reps,
                    })}
                  </Text>
                ) : null}
              </View>
              {er.topE1rmKg !== null || er.isPrE1rm ? (
                <View style={styles.champValueRow}>
                  {er.isPrE1rm ? (
                    <Icon
                      name="trophy"
                      size={theme.typography.sizes.base}
                      color={theme.palette.pr}
                      fill={theme.palette.pr}
                      label={t("mobile.summary.prBadge", {
                        defaultValue: "personal record",
                      })}
                    />
                  ) : null}
                  <Text style={styles.champValue} numberOfLines={1}>
                    {er.topE1rmKg !== null
                      ? formatE1rm(er.topE1rmKg, unit, locale, unitLabels)
                      : ""}
                  </Text>
                </View>
              ) : null}
            </View>
          ))}
        </MetricBlock>
      ) : null}

      {/* Volume/density are "no weighted work happened" when the session is
          cardio/timed-only (kind="cardio" never contributes volume — see
          setMetrics.ts). Showing "Volume 0 kg" there isn't wrong, but it's
          the empty-state-as-zero the summary shouldn't render — the slot is
          unavailable, not zero (RW-2, [[feedback_empty_vs_unresolved_collection]]).
          Duration stays unconditional: it's meaningful for every completed
          session regardless of what was logged. */}
      {result.totalVolumeKg > 0 ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockVolume", { defaultValue: "Volume" })}
          value={formatVolume(result.totalVolumeKg, unit, locale, unitLabels)}
        />
      ) : null}

      <MetricBlock
        theme={theme}
        title={t("mobile.postSession.blockDuration", { defaultValue: "Duration" })}
        value={formatDuration(result.durationMinutes)}
      />

      {result.densityKgPerMin !== null && result.densityKgPerMin > 0 ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockDensity", { defaultValue: "Density" })}
          value={formatDensity(result.densityKgPerMin, unit, locale, unitLabels)}
        />
      ) : null}

      {energySlot}

      {notes.length > 0 ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockNotes", { defaultValue: "Notes" })}
        >
          {notes.map((n) => (
            // One element for a screen reader: the time, then the text.
            <View
              key={n.id}
              style={styles.noteRow}
              accessible
              accessibilityLabel={`${noteTime(n.performedAt)}, ${n.text}`}
            >
              <Text style={styles.noteTime}>{noteTime(n.performedAt)}</Text>
              <Text style={styles.noteText}>{n.text}</Text>
            </View>
          ))}
        </MetricBlock>
      ) : null}

      {/* Layer 3 — Volume bars */}
      {topMuscles.length > 0 ? (
        <View style={styles.barsBlock}>
          <Text style={styles.sectionLabel}>
            {t("mobile.postSession.barsTitle", { defaultValue: "Work by muscle" })}
          </Text>
          {topMuscles.map(([slug, volume]) => {
            const fillPct =
              maxMuscleVolume > 0 ? (volume / maxMuscleVolume) * 100 : 0;
            return (
              <View key={slug} style={styles.barRow}>
                <View style={styles.barTextRow}>
                  <Text style={styles.barLabel} numberOfLines={1}>
                    {t(muscleKey(slug))}
                  </Text>
                  <Text style={styles.barValue}>
                    {formatVolume(volume, unit, locale, unitLabels)}
                  </Text>
                </View>
                <View style={styles.barTrack}>
                  <View
                    style={[styles.barFill, { width: `${Math.max(2, fillPct)}%` }]}
                  />
                </View>
              </View>
            );
          })}
        </View>
      ) : null}

      {/* Layer 4 — Dual bodymap (native-only slot) */}
      {bodyMapSlot ? (
        <View style={styles.bodyBlock}>
          <Text style={styles.sectionLabel}>
            {t("mobile.postSession.bodyTitle", { defaultValue: "Muscles worked" })}
          </Text>
          <View style={styles.bodyWrap}>{bodyMapSlot}</View>
          <ScaleLegend variant="ramp" theme={theme} stops={theme.heatmapRamp} />
        </View>
      ) : null}

      {/* Share affordance (native-only slot) */}
      {shareSlot ?? null}
    </View>
  );
}
